import React, { useEffect, useId, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';

declare global {
  interface Window {
    IdocLoader?: new (lang: string) => {
      initComponent: (
        componentName: string,
        session: Record<string, unknown>,
        containerId: string,
        callback?: (result: unknown) => void,
      ) => void;
    };
  }
}

const LOAD_TIMEOUT_MS = 25_000;
const scriptPromises = new Map<string, Promise<void>>();

function loadIdocScript(src: string): Promise<void> {
  if (window.IdocLoader) return Promise.resolve();
  const cached = scriptPromises.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => (window.IdocLoader ? resolve() : reject(new Error('IdocLoader ausente')));
    script.onerror = () => reject(new Error('Falha ao baixar o script do iDoc'));
    document.head.appendChild(script);
  });
  promise.catch(() => scriptPromises.delete(src));
  scriptPromises.set(src, promise);
  return promise;
}

interface IdocViewerProps {
  scriptUrl: string;
  session: Record<string, unknown>;
  minHeight?: number | string;
  /** Alternativa quando o componente não carrega (ex.: domínio ainda não liberado). */
  onOpenPortal?: () => void;
}

/** Renderiza o PacComp (visualizador oficial do iDoc) dentro do Klinvia. */
const IdocViewer: React.FC<IdocViewerProps> = ({ scriptUrl, session, minHeight = 520, onOpenPortal }) => {
  const containerId = `idoc-pac-comp-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    setState('loading');

    const timeout = window.setTimeout(() => {
      if (!cancelled) setState((s) => (s === 'loading' ? 'error' : s));
    }, LOAD_TIMEOUT_MS);

    loadIdocScript(scriptUrl)
      .then(() => {
        if (cancelled || !container || !window.IdocLoader) return;
        container.innerHTML = '';
        const loader = new window.IdocLoader('pt-BR');
        loader.initComponent('idoc-pac-comp', session, containerId, () => {
          if (!cancelled) setState('ready');
        });
        // Alguns builds do componente não chamam o callback; o conteúdo aparecendo basta.
        const observer = new MutationObserver(() => {
          if (container.childElementCount > 0 && !cancelled) {
            setState('ready');
            observer.disconnect();
          }
        });
        observer.observe(container, { childList: true });
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      if (container) container.innerHTML = '';
    };
  }, [scriptUrl, session, containerId, attempt]);

  return (
    <Box sx={{ position: 'relative', minHeight, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
      <Box id={containerId} ref={containerRef} sx={{ minHeight, visibility: state === 'error' ? 'hidden' : 'visible' }} />
      {state === 'loading' && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: 'rgba(255,255,255,0.85)' }}>
          <CircularProgress size={32} sx={{ color: '#0D9488' }} />
          <Typography variant="body2" color="text.secondary">Abrindo o visualizador iDoc…</Typography>
        </Box>
      )}
      {state === 'error' && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Alert
            severity="warning"
            sx={{ maxWidth: 520 }}
            action={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button size="small" startIcon={<RefreshIcon />} onClick={() => setAttempt((n) => n + 1)}>
                  Tentar de novo
                </Button>
                {onOpenPortal && (
                  <Button size="small" startIcon={<OpenInNewIcon />} onClick={onOpenPortal}>
                    Abrir no portal
                  </Button>
                )}
              </Box>
            }
          >
            Não foi possível carregar o visualizador do iDoc aqui. Se persistir, o domínio do Klinvia ainda não foi
            liberado pela Radio Memory — use a galeria ou abra a documentação no portal.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default IdocViewer;
